<template>
  <VideoTimer
    v-if="name === 'timer'"
    :time="currentTime"
    :duration="duration"
    :disabled="!canPlay"
    @change="changeTime"
  />

  <VideoVolume
    v-else-if="name === 'volume'"
    ref="volumeRef"
    :volume="currentVolume"
    @change="changeVolume"
  />

  <VideoControl
    v-else-if="controlAttrs"
    :class="controlAttrs.class || ns.e(name)"
    :label="controlAttrs.label"
    :shortcut="controlAttrs.shortcut"
    :disabled="controlAttrs.disabled"
    :type="controlAttrs.type"
    :value="controlAttrs.value"
    :options="controlAttrs.options"
    @button-click="controlAttrs.onClick"
    @select="controlAttrs.onSelect"
  >
    <lt-icon v-if="name === 'play-prev'" :size="18"><Back /></lt-icon>
    <lt-icon v-if="name === 'play-next'" :size="18"><Right /></lt-icon>
    <lt-icon v-if="name === 'play'" :size="18">
      <VideoPause v-if="playing" />
      <VideoPlay v-else />
    </lt-icon>
    <lt-icon v-if="name === 'refresh'" :size="18"><Refresh /></lt-icon>

    <img
      v-if="name === 'flip'"
      src="https://image.whzb.com/chain/lit-element/js/img/flip.png"
      alt=""
      style="width: 18px"
    />

    <img
      v-if="name === 'pip'"
      src="https://image.whzb.com/chain/lit-element/js/img/pip.png"
      alt=""
      style="width: 18px"
    />
    <img
      v-if="name === 'full-window'"
      src="https://image.whzb.com/chain/lit-element/js/img/fullWindow.png"
      alt=""
      style="width: 16px"
    />

    <img
      v-if="name === 'full-browser'"
      src="https://image.whzb.com/chain/lit-element/js/img/fullscreen.png"
      alt=""
      style="width: 16px"
    />
  </VideoControl>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { decimalLength } from '@lit-element/components/captcha/src/hooks/number'
import { useNamespace } from '@lit-element/hooks'
import { LtIcon } from '@lit-element/components/icon'
import {
  Back,
  Refresh,
  Right,
  VideoPause,
  VideoPlay,
} from '@inte-cloud/icons-vue'
import VideoControl from './video-control.vue'
import VideoTimer from './video-timer.vue'
import { localeVideo } from './symbol'
import VideoVolume from './video-volume.vue'
import type { VideoControlConfig, VideoPlaybackRate } from './symbol'
import type { PropType } from 'vue'

const props = defineProps({
  name: String as PropType<VideoControlConfig>,
  playPrevDisabled: Boolean,
  playNextDisabled: Boolean,
  currentTime: Number,
  currentVolume: Number,
  pip: Boolean,
  canPlay: Boolean,
  playing: Boolean,
  duration: Number,
  shortcuts: Object,
  playbackRates: Array,
  playbackRate: Number,
  full: [String, Boolean],
})

const emit = defineEmits([
  'next',
  'toggleFull',
  'refresh',
  'prev',
  'togglePlaying',
  'changeRate',
  'changeTime',
  'togglePip',
  'changeVolume',
  'toggleFlip',
])

const ns = useNamespace('video')
const currentRate = ref(props.playbackRate)

const volumeRef = ref<InstanceType<typeof VideoVolume>>()

const controlAttrs = computed<any>(() => {
  const n: any = props.name
  const shortcut = props.shortcuts?.[n]
  if (n === 'play-prev') {
    return {
      label: localeVideo.playPrev,
      shortcut,
      disabled: !!props.playPrevDisabled,
      onClick: playPrev,
    }
  }
  if (n === 'play') {
    return {
      label: props.playing ? localeVideo.pause : localeVideo.play,
      shortcut,
      disabled: !props.canPlay,
      onClick: togglePlaying,
    }
  }
  if (n === 'play-next') {
    return {
      label: localeVideo.playNext,
      shortcut,
      disabled: !!props.playNextDisabled,
      onClick: playNext,
    }
  }

  if (n === 'refresh') {
    return {
      class: ns.e('play-refresh'),
      label: localeVideo.refresh,
      shortcut,
      onClick: handleRefresh,
    }
  }
  if (n === 'playback-rate') {
    return {
      type: 'select',
      value: currentRate.value,
      options: rateOptions.value,
      onSelect: (rate: VideoPlaybackRate) => changeRate(rate.value),
    }
  }

  if (n === 'flip') {
    return {
      label: localeVideo.flip,
      shortcut,
      onClick: toggleFlip,
    }
  }
  if (n === 'pip') {
    return {
      label: props.pip ? localeVideo.exitPip : localeVideo.requestPip,
      shortcut,
      disabled: !props.canPlay,
      onClick: togglePip,
    }
  }

  if (n === 'full-window') {
    return {
      label:
        props.full === 'window'
          ? localeVideo.fullWindowExit
          : localeVideo.fullWindow,
      shortcut,
      onClick: () => toggleFull('onlyShowContainer'),
    }
  }
  if (n === 'full-browser') {
    return {
      label:
        props.full === 'browser'
          ? localeVideo.fullScreenExit
          : localeVideo.fullScreen,
      shortcut,
      onClick: () => toggleFull('fullScreen'),
    }
  }
  return null
})

const rateOptions = computed(() => {
  const rates =
    props.playbackRates
      ?.map((raw: any) => {
        const rate = typeof raw === 'number' ? { value: raw } : raw

        rate.value = Number(rate.value)
        rate.label =
          rate.label ||
          `${decimalLength(rate.value) ? rate.value : rate.value.toFixed(1)}x`

        return rate
      })
      .filter((rate: any) => rate.value > 0)
      .sort((prev: any, next: any) => next.value - prev.value) || []

  if (!rates.some((rate: any) => rate.value === 1)) {
    const index = rates.findIndex((rate: any) => rate.value < 1)

    rates.splice((index + rates.length) % rates.length, 0, {
      value: 1,
      label: '1.0x',
    })
  }
  return rates
})

watch(
  () => props.playbackRate,
  (value: any) => changeRate(value, false)
)

function togglePlaying() {
  emit('togglePlaying', !props.playing)
}

function togglePip() {
  emit('togglePip')
}

function changeRate(rate: number, dispatch = true) {
  currentRate.value = rate
  emit('changeRate', rate, dispatch)
}

function changeTime(time: number, dispatch = true) {
  emit('changeTime', time, dispatch)
}

function changeVolume(volume: number, dispatch = true) {
  emit('changeVolume', volume, dispatch)
}

function toggleFlip(value: any, dispatch = true) {
  emit('toggleFlip', value, dispatch)
}

function playPrev() {
  emit('prev')
}

function playNext() {
  emit('next')
}

function toggleFull(type: any) {
  emit('toggleFull', type)
}

function handleRefresh() {
  emit('refresh')
}

defineExpose({
  volumeRef,
})
</script>
